import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PROCESS } from "./siteData";

const EASE = [0.22, 1, 0.36, 1];
const ICONS = [
  ["M100 30 L140 110 L100 170 L60 110 Z", "M100 30 V105", "M100 107 a9 9 0 1 0 0.1 0", "M100 170 V188"],
  ["M60 80 V40 H140 V80", "M45 80 H155 V140 H45 Z", "M65 120 H135 V172 H65 Z", "M78 140 H122 M78 156 H110"],
  ["M100 20c10 30 45 45 45 95a45 45 0 0 1-90 0c0-28 15-40 22-55 5 18 12 26 20 30-4-25 0-48 3-70z", "M100 160a20 20 0 0 1-20-20c0-14 10-20 14-30 4 12 26 18 26 32a20 20 0 0 1-20 18z"],
  ["M70 132 a18 18 0 1 0 0.1 0", "M130 132 a18 18 0 1 0 0.1 0", "M80 136 L142 36", "M120 136 L58 36"],
  ["M58 156 L152 38", "M145 46 l6 -6", "M146 44 C 185 85, 35 105, 72 165 S 165 160, 150 128"],
  ["M100 38 L160 68 V140 L100 170 L40 140 V68 Z", "M40 68 L100 98 L160 68", "M100 98 V170", "M70 53 L130 83"],
  ["M100 30 L155 50 V100 C155 140 130 160 100 172 C70 160 45 140 45 100 V50 Z", "M78 100 L95 117 L125 85"],
  ["M28 70 H120 V140 H28 Z", "M120 92 H150 L172 118 V140 H120", "M62 152 a12 12 0 1 0 0.1 0", "M146 152 a12 12 0 1 0 0.1 0"],
];

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Pinyon+Script&display=swap');
@keyframes mt-draw{from{stroke-dashoffset:700}to{stroke-dashoffset:0}}
@keyframes mt-shine{0%{background-position:150% 0}100%{background-position:-50% 0}}
@keyframes mt-sew{to{stroke-dashoffset:-24}}
@keyframes mt-glow{0%,100%{opacity:.4}50%{opacity:.85}}
.mt-draw path{fill:none;stroke:#f1cf98;stroke-width:1.6;stroke-linejoin:round;stroke-linecap:round;stroke-dasharray:700;stroke-dashoffset:700;animation:mt-draw 2.2s ease forwards}
.mt-shine{display:inline-block;padding:0 .15em;background:linear-gradient(110deg,#d9a86f 35%,#fff0d2 50%,#d9a86f 65%);background-size:250% 100%;-webkit-background-clip:text;background-clip:text;color:transparent;animation:mt-shine 3.5s linear infinite}
.mt-sew path{stroke-dasharray:6 6;animation:mt-sew 1.2s linear infinite}
.mt-glow{animation:mt-glow 3s ease-in-out infinite}
.mt-tape{background:linear-gradient(#f4e6c6,#e5d0a2)}
.mt-minor{background:linear-gradient(90deg,#3b2a1c 1px,transparent 1px) 0 0/calc(100%/120) 12px repeat-x}
.mt-major{background:linear-gradient(90deg,#3b2a1c 1.5px,transparent 1.5px) 0 0/calc(100%/21) 22px repeat-x}
.mt-num{-webkit-text-stroke:1px rgba(241,207,152,.85);color:transparent}
@media (prefers-reduced-motion:reduce){.mt-shine,.mt-sew path,.mt-glow{animation:none}.mt-draw path{animation:none;stroke-dashoffset:0}}
`;

export default function MfgTape() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setActive((a) => (a + 1) % PROCESS.length), 3200);
    return () => clearInterval(t);
  }, [paused]);
  const step = PROCESS[active] || PROCESS[0];
  const pos = `${(active + 0.5) * (100 / PROCESS.length)}%`;

  return (
    <section className="relative overflow-hidden py-16 text-[#FAF3E8] lg:py-20"
      style={{ background: "linear-gradient(135deg,#7a5a42 0%,#5e4433 55%,#4d382a 100%)" }}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid="mfg-tape">
      <style>{CSS}</style>
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at 75% 40%,rgba(255,228,184,.18),transparent 55%)" }} />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-12">
        <motion.span initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
          className="inline-flex items-center gap-4 text-[0.7rem] uppercase tracking-[0.32em] text-[#f1d7ae]">
          <span className="h-px w-12 bg-[#f1d7ae]" />Manufacturing Excellence
        </motion.span>
        <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.15, ease: EASE }}
          className="mt-6 font-display text-[clamp(2.2rem,4.6vw,4rem)] font-light leading-[1.08]">
          From artwork to dispatch, under one{" "}
          <span className="mt-shine" style={{ fontFamily: "'Pinyon Script', cursive", fontSize: "1.3em" }}>roof.</span>
        </motion.h2>

        {/* Stage display */}
        <div className="mt-14 grid items-center gap-8 lg:grid-cols-[300px_1fr_260px] lg:gap-12">
          <AnimatePresence mode="wait">
            <motion.div key={`n${active}`} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.6, ease: EASE }}
              className="mt-num font-display text-[140px] font-light leading-[.8] tracking-[-6px] lg:text-[260px]">
              0{active + 1}
            </motion.div>
          </AnimatePresence>
          <AnimatePresence mode="wait">
            <motion.div key={`t${active}`} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.6, ease: EASE }}>
              <div className="text-[11px] tracking-[0.35em] text-[#f1d7ae]">STAGE 0{active + 1} OF 0{PROCESS.length}</div>
              <h3 className="mb-4 mt-3 font-display text-[clamp(2rem,4vw,3.6rem)] font-light leading-tight">{step.title}</h3>
              <p className="max-w-md text-[1.05rem] font-light leading-[1.8] text-[#ecdcc6]">{step.text}</p>
              <svg className="mt-sew mt-6 block" width="240" height="20" viewBox="0 0 240 20" aria-hidden="true">
                <path d="M0 10 C 60 0, 110 20, 170 10 S 225 2, 240 10" fill="none" stroke="#f1cf98" strokeWidth="1.4" />
              </svg>
            </motion.div>
          </AnimatePresence>
          <div className="relative mx-auto hidden h-[240px] w-[240px] lg:block">
            <div className="absolute inset-0 rounded-full border border-[#f1cf98]/40" />
            <div className="mt-glow absolute inset-5 rounded-full" style={{ background: "radial-gradient(circle,rgba(255,228,184,.3),transparent 70%)" }} />
            <svg key={`i${active}`} viewBox="0 0 200 200" className="mt-draw absolute inset-7" aria-hidden="true">
              {(ICONS[active] || ICONS[0]).map((d, k) => <path key={k} d={d} />)}
            </svg>
          </div>
        </div>

        {/* Measuring tape */}
        <div className="relative mt-16 pt-16 lg:pt-20">
          {PROCESS.map((p, i) => (
            <button key={p.title} type="button" onClick={() => setActive(i)}
              className="absolute top-0 w-[14.28%] -translate-x-1/2 text-center transition-colors duration-500" style={{ left: `${(i + 0.5) * (100 / PROCESS.length)}%` }}>
              <b className={`block text-[10px] font-normal tracking-[0.3em] ${i === active ? "text-[#ffe2b0]" : "text-[#d4bc9c]"}`}>0{i + 1}</b>
              <span className={`mt-1.5 hidden font-display leading-tight md:block ${i === active ? "text-[1.3rem] text-white" : i < active ? "text-lg text-[#ecdcc6]" : "text-lg text-[#c9ae8c]"}`}>{p.title}</span>
            </button>
          ))}
          <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 1.5, ease: EASE }}
            className="mt-tape relative h-11 origin-left overflow-hidden rounded-[3px] shadow-[0_18px_40px_rgba(30,18,10,.35)]">
            <div className="mt-minor absolute inset-x-0 top-0 h-3" />
            <div className="mt-major absolute inset-x-0 top-0 h-[22px]" />
            <div className="absolute inset-x-0 bottom-1 flex text-[10px] text-[#3b2a1c]">
              {Array.from({ length: 21 }, (_, n) => <span key={n} className="flex-1 pl-1">{n}</span>)}
            </div>
            <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-transparent to-[#8a5a32]/35 transition-[width] duration-1000" style={{ width: pos }} />
          </motion.div>
          <div className="absolute top-[52px] h-20 w-0.5 -translate-x-1/2 bg-[#ffe2b0] shadow-[0_0_14px_rgba(255,226,176,.9)] transition-[left] duration-1000 ease-out lg:top-[68px]" style={{ left: pos }}>
            <span className="absolute -top-2 left-1/2 h-3 w-3 -translate-x-1/2 rounded-full bg-[#ffe2b0]" />
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
          <Link to="/manufacturing" data-testid="home-manufacturing-link"
            className="inline-flex items-center gap-3 rounded-full border border-[#f1d7ae] px-7 py-3.5 text-xs tracking-[0.3em] text-[#FAF3E8] transition-colors duration-500 hover:bg-[#f1d7ae] hover:text-[#3b2a1c]">
            SEE THE FULL PROCESS <span aria-hidden="true">↗</span>
          </Link>
          <span className="text-4xl text-[#f1d7ae]/80" style={{ fontFamily: "'Pinyon Script', cursive" }}>measured twice, made once</span>
        </div>
      </div>
    </section>
  );
}
